import {MeteringRecord} from '../types';
import {
    PlatformPropertyCard,
    PlatformPropertyCardCopyButtonDecorator,
    PlatformPropertyCardProperty
} from "@flexinet/ui-components";
import {Code, Stack, Text, Title} from "@mantine/core";
import {StatusBadge} from "./StatusBadge.tsx";
import {prettyPrintAwsJson} from "../utils/parseAwsJson.ts";

const JsonBlock = ({title, value}: { title: string, value: string }) => (
    <Stack gap={'xs'}>
        <Title order={5}>{title}</Title>
        {value
            ? <Code block style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>{value}</Code>
            : <Text c={'dimmed'} size={'sm'}>—</Text>}
    </Stack>
);

export const Profile = ({record}: { record: MeteringRecord }) => {

    const identityProperties = [
        {
            label: 'Metering Record ID',
            value: record.meteringRecordId as string,
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: 'State',
            value: <StatusBadge status={record.state}/>,
            span: 12,
        },
        {
            label: 'Customer ID',
            value: record.customerId as string,
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: 'Meter ID',
            value: record.meterId as string,
            span: 12,
        },
        {
            label: 'Meter Code',
            value: (record.meterCode as string) ?? '—',
            span: 12,
        },
        {
            label: 'Sort Key',
            value: record.sortKey as string,
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: 'Dimensions Hash',
            value: record.dimensionsHash as string,
            span: 12,
        },
        {
            label: 'Record Type',
            value: record.recordType as string,
            span: 12,
        },
    ] as PlatformPropertyCardProperty[];

    const aggregationProperties = [
        {
            label: 'Value',
            value: typeof record.value === 'number' ? String(record.value) : '—',
            span: 12,
        },
        {
            label: 'Input Event Count',
            value: typeof record.inputEventCount === 'number' ? String(record.inputEventCount) : '—',
            span: 12,
        },
        {
            label: 'Period Start',
            value: record.periodStart as string,
            formatAs: 'Timestamp',
            span: 12,
        },
        {
            label: 'Period End',
            value: record.periodEnd as string,
            formatAs: 'Timestamp',
            span: 12,
        },
        {
            label: 'Watermark',
            value: (record.watermark as string) ?? '—',
            span: 12,
        },
        {
            label: 'Computed At',
            value: (record.computedAt as string) ?? '—',
            span: 12,
        },
        {
            label: 'Created',
            value: record.created as string,
            formatAs: 'Timestamp',
            span: 12,
        },
        {
            label: 'Last Updated',
            value: record.updated as string,
            formatAs: 'Timestamp',
            span: 12,
        },
    ] as PlatformPropertyCardProperty[];

    return (
        <Stack gap={'lg'}>
            <PlatformPropertyCard properties={identityProperties}/>
            <PlatformPropertyCard properties={aggregationProperties}/>

            {/* dimensions is the AWSJSON breakdown the aggregate is keyed on. */}
            <JsonBlock title={'Dimensions'} value={prettyPrintAwsJson(record.dimensions)}/>
        </Stack>
    );
};
